import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import { articles, sportCategories, hotEvents } from '@/mocks/sports';

const categoryNames: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  tennis: '网球',
  esports: '电竞',
  racing: '赛车',
  boxing: '搏击',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categoryNames[article.category] || article.category;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    url: `https://www.bbty.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'bb体育',
      url: 'https://www.bbty.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.bbty.com/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '如何获取bb体育最新赛事资讯？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以访问bb体育官网实时查看最新赛事资讯，也可以通过bb体育app下载获取推送通知。',
        },
      },
      {
        '@type': 'Question',
        name: 'bb体育平台提供哪些体育项目？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'bb体育平台覆盖足球、篮球、网球、电竞、赛车、搏击等超过50个体育项目，为体育爱好者提供一站式服务。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - bb体育`}
        description={article.summary}
        keywords={article.tags.join(',') + ',bb体育'}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {catName}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-1 text-xs bg-background-100 text-foreground-500 rounded-full">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-xs"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>

            <div className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.content }} />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900">分享文章</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="p-4">
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{ra.summary}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                        <span>{ra.publishDate}</span>
                        <span>{ra.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
              常见问题
            </h3>
            <div className="space-y-3">
              <details className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                  如何获取bb体育最新赛事资讯？
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                  您可以访问bb体育官网实时查看最新赛事资讯，也可以通过bb体育app下载获取推送通知，随时掌握赛场动态。
                </div>
              </details>
              <details className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                  bb体育平台提供哪些体育项目？
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                  bb体育平台覆盖足球、篮球、网球、电竞、赛车、搏击等超过50个体育项目，为体育爱好者提供一站式体育娱乐体验。
                </div>
              </details>
              <details className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                  如何注册bb体育账号？
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                  您可以通过bb体育官网或bb体育app进行注册，填写基本信息即可完成注册，享受全面的赛事资讯和直播服务。
                </div>
              </details>
            </div>
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}